import { c as _c } from "react/compiler-runtime";
import { Box } from "@mui-wrapper";
import { AddCredits } from "@pages/settings/subscription-settings/credits/add-credits";
import { CreditSubscriptions } from "@pages/settings/subscription-settings/credits/credit-subscriptions";
import { CreditsBalanceItem } from "@pages/settings/subscription-settings/credits/credits-balance-item";
import { SubscriptionExpirationAlert } from "@pages/settings/subscription-settings/subscription-expiration-alert";
import { CREDIT_DISPLAY_NAME, CREDITS_DISPLAY_NAME } from "@src/constants/credit-constants";
import { allCreditSubscriptionsSelector, companyCreditBalanceSelector, getEarliestExpiringCreditsSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { nounPluralize } from "@utils/data-display";
import { isCreditsNearExpiry } from "@utils/sdb-company-utils";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { useMemo } from "react";
/** Custom hook to return credit settings props for subscription page */
export function useCreditSettings() {
  const $ = _c(27);
  const creditSubscriptions = useAppSelector(allCreditSubscriptionsSelector);
  const companyCreditBalance = useAppSelector(companyCreditBalanceSelector);
  const earliestExpiringCredits = useAppSelector(getEarliestExpiringCreditsSelector);
  let t0;
  if ($[0] !== earliestExpiringCredits) {
    t0 = isCreditsNearExpiry(earliestExpiringCredits);
    $[0] = earliestExpiringCredits;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isExpiringSoon = t0;
  const shouldShowExistingTokensSection = creditSubscriptions.length > 0;
  let t1;
  bb0: {
    if (earliestExpiringCredits) {
      let t2;
      if ($[2] !== earliestExpiringCredits.totalCredits) {
        t2 = nounPluralize({
          shouldShowCounter: true,
          counter: earliestExpiringCredits.totalCredits,
          word: CREDIT_DISPLAY_NAME
        });
        $[2] = earliestExpiringCredits.totalCredits;
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      t1 = `${t2} expire on`;
      break bb0;
    }
    t1 = `No ${CREDITS_DISPLAY_NAME} available`;
  }
  const subscriptionCreditLabel = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = capitalizeFirstLetter(CREDITS_DISPLAY_NAME);
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = !shouldShowExistingTokensSection;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <AddCredits />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== companyCreditBalance) {
    t5 = {
      label: "Balance",
      content: <CreditsBalanceItem creditBalance={companyCreditBalance} />,
      shouldHideTopBorder: true
    };
    $[6] = companyCreditBalance;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== earliestExpiringCredits || $[9] !== isExpiringSoon) {
    t6 = <CreditSubscriptions earliestExpiringCredits={earliestExpiringCredits} isExpiringSoon={isExpiringSoon} />;
    $[8] = earliestExpiringCredits;
    $[9] = isExpiringSoon;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== isExpiringSoon || $[12] !== subscriptionCreditLabel || $[13] !== t6) {
    t7 = {
      label: subscriptionCreditLabel,
      content: t6,
      shouldHideBottomBorder: isExpiringSoon
    };
    $[11] = isExpiringSoon;
    $[12] = subscriptionCreditLabel;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Box sx={{
      height: "70px",
      width: "100%"
    }}><SubscriptionExpirationAlert description={`Your ${CREDITS_DISPLAY_NAME} are expiring soon! Use them before they expire, or you'll lose them.`} /></Box>;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = !isExpiringSoon;
  let t10;
  if ($[16] !== t9) {
    t10 = {
      content: t8,
      shouldHideBottomBorder: true,
      shouldContentTakeFullWidth: true,
      isHidden: t9
    };
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t5 || $[20] !== t7) {
    t11 = [t5, t7, t10];
    $[18] = t10;
    $[19] = t5;
    $[20] = t7;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t3) {
    t12 = [{
      title: t2,
      subtitle: `Use and share ${CREDITS_DISPLAY_NAME} with your team to generate floor plans within your projects. 
        Explore the different ${CREDIT_DISPLAY_NAME} bundles we offer.`,
      isHidden: t3,
      footerAction: t4,
      items: t11
    }];
    $[22] = t11;
    $[23] = t3;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const creditsSectionProps = t12;
  let t13;
  if ($[25] !== creditsSectionProps) {
    t13 = {
      creditsSectionProps
    };
    $[25] = creditsSectionProps;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}